import { useCallback } from 'react';
import {
  createBlock,
  findBlock,
  updateBlockInTree,
  removeBlockFromTree,
  insertBlockAfter,
  flattenBlocks,
  flattenNavigableBlocks,
  cleanupEmptyContainers,
} from '../../../utils/blockUtils';
import { focusEditable } from '../../../utils/focusUtils';
import { EDITABLE_TYPES, NAVIGABLE_TYPES } from '../../../utils/editorConstants';
import { parentOf } from '../../../utils/blockTreeHelpers';
import {
  buildUncheckAllTickOverrides,
  clearCheckedBoxes,
  isTickActive,
  readMasterTick,
  tickOverridesEqual,
} from '../../../utils/tickOverrides';

/** Update / insert / delete blocks and related helpers (navigate, uncheck). */
export default function useBlockMutations({
  blocks,
  blocksRef,
  commit,
  tickOverridesRef,
  onTickOverridesChangeRef,
  pushHistory,
  applyTickOverrides,
}) {
  const handleUpdate = useCallback(
    (blockId, updates, options) => {
      // Pure typing (content only, no type/props changes): coalesce the
      // React-state propagation — the edited block renders from local state
      const isContentOnly =
        Object.keys(updates).length === 1 && typeof updates.content === 'string';
      // Table cell typing updates props.tableData (same grid shape). Treat it
      // like content typing so keystrokes coalesce into one undo step; add /
      // delete row/column changes shape and stays a structural step.
      let isTableCellTyping = false;
      if (
        !isContentOnly &&
        Object.keys(updates).length === 1 &&
        updates.props?.tableData
      ) {
        const prev = findBlock(blocksRef.current, blockId)?.props?.tableData;
        const next = updates.props.tableData;
        const sameShape =
          !!prev &&
          !!next &&
          (prev.headers?.length || 0) === (next.headers?.length || 0) &&
          (prev.rows?.length || 0) === (next.rows?.length || 0);
        const sameWidths =
          JSON.stringify(prev?.columnWidths || null) ===
          JSON.stringify(next?.columnWidths || null);
        isTableCellTyping = sameShape && sameWidths;
      }
      // Checkbox ticks are structural (own undo step), same as Uncheck all.
      // A paint-drag passes record:false after the first box so the gesture
      // is one undo step.
      commit(updateBlockInTree(blocksRef.current, blockId, updates), {
        coalesce: isContentOnly || isTableCellTyping,
        record: options?.record !== false,
      });
    },
    [commit, blocksRef]
  );

  const handleInsertAfter = useCallback(
    (targetId, typeOrBlock = 'text') => {
      const newBlock = typeof typeOrBlock === 'string' ? createBlock(typeOrBlock) : typeOrBlock;
      commit(insertBlockAfter(blocksRef.current, targetId, newBlock));
      if (EDITABLE_TYPES.includes(newBlock.type)) focusEditable(newBlock.id);
    },
    [commit, blocksRef]
  );

  const handleUpdateAndInsert = useCallback(
    (blockId, updates, newBlock, { focusAtStart = false, focusNewBlock = true } = {}) => {
      let tree = updateBlockInTree(blocksRef.current, blockId, updates);
      tree = insertBlockAfter(tree, blockId, newBlock);
      commit(tree);
      if (focusNewBlock && EDITABLE_TYPES.includes(newBlock.type)) {
        focusEditable(newBlock.id, { atStart: focusAtStart });
      }
    },
    [commit, blocksRef]
  );

  const handleDelete = useCallback(
    (blockId) => {
      const current = blocksRef.current;
      const flat = flattenBlocks(current);
      const editable = flat.filter((b) => EDITABLE_TYPES.includes(b.type));
      if (editable.length <= 1 && editable.some((b) => b.id === blockId)) return;

      const parent = parentOf(current, blockId);
      const idToRemove =
        parent?.type === 'callout' && parent.children.length === 1 ? parent.id : blockId;

      const index = flat.findIndex((b) => b.id === idToRemove);
      const previous = flat
        .slice(0, Math.max(index, 0))
        .reverse()
        .find((b) => EDITABLE_TYPES.includes(b.type) && b.id !== blockId);

      commit(cleanupEmptyContainers(removeBlockFromTree(current, idToRemove)));
      if (previous) focusEditable(previous.id);
    },
    [commit, blocksRef]
  );

  const handleNavigate = useCallback(
    (blockId, direction) => {
      const editables = flattenNavigableBlocks(blocksRef.current, NAVIGABLE_TYPES);
      const index = editables.findIndex((b) => b.id === blockId);
      if (index === -1) return false;
      const backward = direction === 'up' || direction === 'left';
      const target = backward ? editables[index - 1] : editables[index + 1];
      if (!target) return false;
      // left/up → end of previous; right/down → start of next
      focusEditable(target.id, { atStart: !backward });
      return true;
    },
    [blocksRef]
  );

  const hasCheckedBoxes = flattenBlocks(blocks).some(
    (b) => b.type === 'checkbox' && isTickActive(readMasterTick(b))
  );

  const uncheckAll = useCallback(() => {
    // Mission run-state: ticks live in overrides, not the block tree.
    if (onTickOverridesChangeRef?.current) {
      const next = buildUncheckAllTickOverrides(blocksRef.current);
      if (tickOverridesEqual(tickOverridesRef.current, next)) return;
      pushHistory(false);
      applyTickOverrides(next);
      return;
    }
    commit(clearCheckedBoxes(blocksRef.current));
  }, [
    commit,
    blocksRef,
    tickOverridesRef,
    onTickOverridesChangeRef,
    pushHistory,
    applyTickOverrides,
  ]);

  return {
    handleUpdate,
    handleInsertAfter,
    handleUpdateAndInsert,
    handleDelete,
    handleNavigate,
    hasCheckedBoxes,
    uncheckAll,
  };
}
